import { motion } from "framer-motion";
import { FileText, Instagram, ExternalLink, Download, ClipboardList } from "lucide-react";

const FosterResources = () => {
  const resources = [
    {
      icon: FileText,
      title: "Foster Resource Documents",
      description: "Access our comprehensive foster guide, medical protocols, and helpful tips for caring for your foster pet.",
      link: "https://drive.google.com/drive/folders/1sifckzsap_X226ejLjO2AUdGIHQGOFXX?usp=sharing",
      buttonText: "View Documents",
    },
    {
      icon: Instagram,
      title: "Foster Stronger Community",
      description: "Connect with other fosters, share tips, and find support in our Foster Stronger Instagram community.",
      link: "https://www.instagram.com/foster_stronger/",
      buttonText: "Follow on Instagram",
    },
    {
      icon: ClipboardList,
      title: "Foster Reimbursement Form",
      description: "Submit receipts for approved foster expenses. We've got your back with supplies and veterinary care.",
      link: "https://docs.google.com/forms/d/e/1FAIpQLSf2oMzt9jhlPGvtkDRE4ZfDSmWdI6z35uA_YuRCZBbmth28-w/viewform?usp=header",
      buttonText: "Submit Reimbursement",
    },
  ];

  return (
    <div data-testid="foster-resources-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              For Our Foster Families
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Foster Resources
            </h1>
            <p className="text-xl text-[#A3A3A3] max-w-2xl mx-auto">
              Everything you need to provide the best care for your foster pet. Thank you for being part of the rescue family.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={resource.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-tactical p-8 flex flex-col h-full"
              >
                <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center mb-6">
                  <resource.icon className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-3">{resource.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6 flex-grow">{resource.description}</p>
                <a
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-${index}-link`}
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  {resource.buttonText}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Foster Tips */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Quick Guide
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white">
              Foster Tips
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Decompression Period",
                tips: [
                  "Give your foster at least 2 weeks to decompress",
                  "Keep things quiet and calm initially",
                  "Establish a consistent routine",
                  "Let them come to you on their terms",
                ],
              },
              {
                title: "Health & Safety",
                tips: [
                  "Keep fosters separate from resident pets initially",
                  "Monitor eating, drinking, and bathroom habits",
                  "Contact us immediately for any health concerns",
                  "Keep all vet records and receipts",
                ],
              },
              {
                title: "Communication",
                tips: [
                  "Send regular updates and photos",
                  "Report any behavioral changes",
                  "Ask questions—no question is too small",
                  "Join our foster support community",
                ],
              },
              {
                title: "Preparing for Adoption",
                tips: [
                  "Note personality traits and quirks",
                  "Document what works for training",
                  "Share insights about ideal home environment",
                  "Help with meet-and-greets when possible",
                ],
              },
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border border-[#4A5320] p-6 bg-[#0A0A0A]"
              >
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.tips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-[#A3A3A3] text-sm">
                      <span className="w-1.5 h-1.5 bg-[#FFD700] mt-2 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="card-tactical p-8 lg:p-12 text-center"
          >
            <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-4">
              Need Help?
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-6">
              For urgent foster concerns, don&apos;t hesitate to reach out immediately.
            </p>
            <a
              href="mailto:info@3eyeblindrescue.org"
              className="text-[#FFD700] text-lg font-mono hover:underline"
            >
              info@3eyeblindrescue.org
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#4A5320]/20 border-y border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-6">
            Not a Foster Yet?
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Fostering saves lives. If you&apos;re interested in becoming a foster, we&apos;d love to have you on the team.
          </p>
          <a
            href="https://rescuesupportkit.com/rescue/3eyeblind/applications"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="become-foster-button"
            className="btn-primary inline-flex items-center gap-2"
          >
            Apply to Foster
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default FosterResources;
